<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
  >
    <ReportTable minWidth="760px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>Insurance payments</span></th>
          <th><span>Patient payments</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Net collections"
              :tooltip="tooltips['Net collections']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && collectionsPerProvider.length">
          <tr
            v-for="(item, index) in collectionsPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td>{{ formatMoney(item.insurancePayments) }}</td>
            <td>{{ formatMoney(item.patientPayments) }}</td>
            <td>{{ formatMoney(item.netCollection) }}</td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td>{{ formatMoney(median.insurancePayments) }}</td>
            <td>{{ formatMoney(median.patientPayments) }}</td>
            <td>{{ formatMoney(median.netCollection) }}</td>
          </tr>

          <tr>
            <td>Whole clinic</td>
            <td>{{ formatMoney(clinicMetricsByName.insurancePayments) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.patientPayments) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netCollection) }}</td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td>{{ formatMoney(userRecord.insurancePayments) }}</td>
            <td>{{ formatMoney(userRecord.patientPayments) }}</td>
            <td>{{ formatMoney(userRecord.netCollection) }}</td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td>{{ formatMoney(median.insurancePayments) }}</td>
            <td>{{ formatMoney(median.patientPayments) }}</td>
            <td>{{ formatMoney(median.netCollection) }}</td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td>{{ formatMoney(averageTop10Percent.insurancePayments) }}</td>
            <td>{{ formatMoney(averageTop10Percent.patientPayments) }}</td>
            <td>{{ formatMoney(averageTop10Percent.netCollection) }}</td>
          </tr>

          <tr v-if="clinicMetricsByName">
            <td>Whole clinic</td>
            <td>{{ formatMoney(clinicMetricsByName.insurancePayments) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.patientPayments) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netCollection) }}</td>
          </tr>
        </template>

        <NoRecordsFound
          v-else
          colspan="4"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'DDSCollections',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      collectionsPerProviderAll: [],
      clinicMetricsByName: null,
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'Net collections': 'Insurance payments + Patient payments',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    collectionsPerProvider() {
      return this.showHiddenProviders
        ? this.collectionsPerProviderAll
        : this.collectionsPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getDDSCollectionsReport({ start, end });

      if (result.status === 200) {
        const collectionsPerProvider = result.data.collectionsPerProvider ?? [];
        this.collectionsPerProviderAll = collectionsPerProvider;

        Utils.prependCurrentUserRecordAndMedian(
          collectionsPerProvider,
          result.data.median,
        );

        this.clinicMetricsByName = result.data.clinicMetricsByName;
        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.collectionsPerProviderAll = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>
